import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Upload,
  FileText,
  Box,
  Image as ImageIcon,
  File,
  Paperclip,
  Trash2,
  Download,
  Eye,
  Plus,
  Loader2,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getCurrentUser } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import type { PlybookAttachment, AttachmentType } from '@/types/plybook';
import { ATTACHMENT_TYPE_LABELS } from '@/types/plybook';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface PlybookFileUploadProps {
  plybookId: string;
  readOnly?: boolean;
}

const iconMap = {
  pdf: FileText,
  cad: Box,
  image: ImageIcon,
  document: File,
  other: Paperclip,
};

export function PlybookFileUpload({ plybookId, readOnly = false }: PlybookFileUploadProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; attachment?: PlybookAttachment }>({ open: false });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [attachmentType, setAttachmentType] = useState<AttachmentType>('document');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  // Fetch attachments
  const { data: attachments, isLoading } = useQuery({
    queryKey: ['plybook-attachments', plybookId],
    queryFn: async () => {
      const { data, error } = await api
        .from('plybook_attachments')
        .select(`
          *,
          uploaded_by_profile:profiles!plybook_attachments_uploaded_by_fkey(id, full_name)
        `)
        .eq('plybook_id', plybookId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as PlybookAttachment[];
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api
        .from('plybook_attachments')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plybook-attachments', plybookId] });
      toast({ title: 'Archivo eliminado' });
      setDeleteDialog({ open: false });
    },
    onError: (error: any) => {
      toast({ title: 'Error al eliminar', description: error.message, variant: 'destructive' });
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Auto-detect type
      if (file.type === 'application/pdf') {
        setAttachmentType('pdf');
      } else if (file.name.match(/\.(dwg|dxf|step|stp|iges|igs|stl)$/i)) {
        setAttachmentType('cad');
      } else if (file.type.startsWith('image/')) {
        setAttachmentType('image');
      } else {
        setAttachmentType('document');
      }
      setUploadDialogOpen(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const user = getCurrentUser();

      // Upload file to server
      // IMPORTANTE: folder debe ir ANTES de file para que multer lo reciba en destination
      const formData = new FormData();
      formData.append('folder', `plybooks_${plybookId}`);
      formData.append('file', selectedFile);

      const uploadResponse = await fetch('/upload', {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error('Error al subir el archivo al servidor');
      }

      const uploadResult = await uploadResponse.json();
      const fileUrl = uploadResult.url;

      const { error } = await api
        .from('plybook_attachments')
        .insert([{
          plybook_id: plybookId,
          file_name: selectedFile.name,
          file_url: fileUrl,
          file_type: selectedFile.type,
          file_size: selectedFile.size,
          attachment_type: attachmentType,
          description: description || null,
          uploaded_by: user?.id,
        }]);

      if (error) throw error;

      // Si es una imagen y el plybook aun no tiene imagen principal, asignarla automaticamente
      if (attachmentType === 'image') {
        const { data: pb } = await api
          .from('plybooks')
          .select('main_image_url')
          .eq('id', plybookId)
          .single();
        if (pb && !pb.main_image_url) {
          await api.from('plybooks').update({ main_image_url: fileUrl }).eq('id', plybookId);
          queryClient.invalidateQueries({ queryKey: ['plybook', plybookId] });
        }
      }

      queryClient.invalidateQueries({ queryKey: ['plybook-attachments', plybookId] });
      toast({ title: 'Archivo subido correctamente' });
      resetUploadForm();
    } catch (error: any) {
      toast({ title: 'Error al subir archivo', description: error.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setAttachmentType('document');
    setDescription('');
    setUploadDialogOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getIcon = (type: AttachmentType) => {
    const Icon = iconMap[type] || Paperclip;
    return <Icon className="w-5 h-5" />;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/3" />
            <div className="h-16 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Paperclip className="w-4 h-4" />
            Archivos Adjuntos
          </CardTitle>
          {!readOnly && (
            <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} className="gap-2">
              <Plus className="w-4 h-4" />
              Subir Archivo
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.dwg,.dxf,.step,.stp,.iges,.igs,.stl,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
          onChange={handleFileSelect}
        />

        {/* Attachments list */}
        {attachments && attachments.length > 0 ? (
          <div className="space-y-2">
            {attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  {getIcon(attachment.attachment_type)}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{attachment.file_name}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="secondary" className="text-xs">
                      {ATTACHMENT_TYPE_LABELS[attachment.attachment_type]}
                    </Badge>
                    <span>{formatFileSize(attachment.file_size)}</span>
                    <span>•</span>
                    <span>
                      {format(new Date(attachment.created_at), "d MMM yyyy", { locale: es })}
                    </span>
                  </div>
                  {attachment.description && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                      {attachment.description}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => window.open(attachment.file_url, '_blank')}
                    title="Ver archivo"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = attachment.file_url;
                      link.download = attachment.file_name;
                      link.click();
                    }}
                    title="Descargar"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteDialog({ open: true, attachment })}
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Paperclip className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No hay archivos adjuntos</p>
            {!readOnly && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3 gap-2"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-4 h-4" />
                Subir Archivo
              </Button>
            )}
          </div>
        )}
      </CardContent>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={(open) => !open && resetUploadForm()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Subir Archivo</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* File info */}
            {selectedFile && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  {getIcon(attachmentType)}
                </div>
                <div>
                  <p className="font-medium">{selectedFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(selectedFile.size)}
                  </p>
                </div>
              </div>
            )}

            {/* Type selector */}
            <div className="space-y-2">
              <Label>Tipo de Archivo</Label>
              <Select value={attachmentType} onValueChange={(v) => setAttachmentType(v as AttachmentType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF - Especificaciones</SelectItem>
                  <SelectItem value="cad">CAD - Plano/Modelo 3D</SelectItem>
                  <SelectItem value="image">Imagen - Referencia Visual</SelectItem>
                  <SelectItem value="document">Documento - Otro</SelectItem>
                  <SelectItem value="other">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Descripción (opcional)</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ej: Plano de corte versión final"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetUploadForm}>
              Cancelar
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !selectedFile}>
              {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Subir Archivo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar archivo?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará "{deleteDialog.attachment?.file_name}". Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteDialog.attachment && deleteMutation.mutate(deleteDialog.attachment.id)}
              className="bg-destructive text-destructive-foreground"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
